import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, CheckCircle2, AlertCircle, Wand2 } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Phase 25.1.1 — Manual Column Mapping Wizard.
 *
 * Slide-in drawer that lets an Accounting User:
 *  1. Review the auto-detected mapping.
 *  2. Manually pick which Accounting field each imported column represents.
 *  3. See a live preview of the first N raw rows re-shaped to the mapping.
 *  4. (Optional) Save the current mapping as a reusable Template.
 *  5. Confirm — the import lines are rebuilt from the raw rows.
 *
 * The wizard is a full mapping surface: opening it AFTER lines already exist
 * lets users re-map at any time before the import is marked Ready.
 */
const IGNORE_KEY = '__ignore__';

export function MappingWizard({ open, onClose, importId, carrierId, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [fields, setFields] = useState([]);           // [{key, label, required}]
  const [headers, setHeaders] = useState([]);         // [str]
  const [sampleRows, setSampleRows] = useState([]);   // [[val,...]]
  const [totalRows, setTotalRows] = useState(0);
  const [columnMap, setColumnMap] = useState({});     // header -> canonical
  const [suggestedMap, setSuggestedMap] = useState({});
  const [templates, setTemplates] = useState([]);     // ranked
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveTemplateName, setSaveTemplateName] = useState('');

  const requiredFields = useMemo(() => fields.filter((f) => f.required).map((f) => f.key), [fields]);

  const load = useCallback(async () => {
    if (!importId) return;
    setLoading(true);
    try {
      const [fieldsRes, prevRes] = await Promise.all([
        accountingAPI.listFields(),
        accountingAPI.preview(importId, { limit: 10 }),
      ]);
      setFields(fieldsRes.data.fields || []);
      const p = prevRes.data;
      setHeaders(p.headers || []);
      setSampleRows(p.sample_rows || []);
      setTotalRows(p.total_rows || 0);
      setColumnMap(p.column_map || p.suggested_column_map || {});
      setSuggestedMap(p.suggested_column_map || {});
      setTemplates(p.template_matches || []);
      setSelectedTemplateId(p.mapped_template_id || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load mapping preview');
    } finally { setLoading(false); }
  }, [importId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const setMap = (header, value) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (!value || value === IGNORE_KEY) {
        delete next[header];
      } else {
        // Unset any other header currently mapped to this canonical field.
        Object.keys(next).forEach((h) => {
          if (h !== header && next[h] === value) delete next[h];
        });
        next[header] = value;
      }
      return next;
    });
  };

  const applyAutoDetect = () => {
    setColumnMap({ ...suggestedMap });
    toast.info('Auto-detected mapping applied');
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.template_id === tid);
    if (!t) return;
    setColumnMap({ ...(t.column_map || {}) });
    setSelectedTemplateId(tid);
    toast.info(`Template "${t.template_name}" applied`);
  };

  // Compute preview: header -> canonical + sample values from first data row.
  const previewRows = useMemo(() => {
    if (!headers.length) return [];
    const firstRow = sampleRows[0] || [];
    return headers.map((h, i) => ({
      header: h,
      canonical: columnMap[h] || '',
      sampleValues: sampleRows.slice(0, 3).map((r) => r?.[i] ?? ''),
      firstValue: firstRow[i] ?? '',
    }));
  }, [headers, sampleRows, columnMap]);

  const missingRequired = requiredFields.filter(
    (rf) => !Object.values(columnMap).includes(rf),
  );

  const submit = async () => {
    setApplying(true);
    try {
      const payload = { column_map: columnMap };
      if (selectedTemplateId) payload.template_id = selectedTemplateId;
      if (saveTemplateName.trim()) {
        payload.save_as_template = { name: saveTemplateName.trim() };
      }
      const { data } = await accountingAPI.applyMapping(importId, payload);
      toast.success(`Mapping applied — ${data.lines_created} lines rebuilt`);
      if (data.saved_template_name) {
        toast.success(`Template "${data.saved_template_name}" saved for this carrier`);
        setSaveTemplateName('');
      }
      onApplied?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply mapping');
    } finally { setApplying(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v && !applying) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="mapping-wizard">
        <SheetHeader>
          <SheetTitle>Column Mapping</SheetTitle>
          <SheetDescription>
            Pick which Accounting field each imported column represents. Unmapped columns are ignored.
            {totalRows > 0 && <> · <span className="text-slate-700">{totalRows} raw rows</span> in this statement.</>}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="py-16 text-center text-slate-400">
            <Loader2 className="mx-auto animate-spin" />
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            {/* Auto-detect + template controls */}
            <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="min-w-[220px] flex-1">
                <Label className="text-xs">Saved templates for this carrier</Label>
                <Select
                  value={selectedTemplateId || 'none'}
                  onValueChange={(v) => v && v !== 'none' && applyTemplate(v)}
                >
                  <SelectTrigger data-testid="mapping-template-select">
                    <SelectValue placeholder="Choose a saved template…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No template —</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.template_name} · match {Math.round((t.score || 0) * 100)}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={applyAutoDetect} data-testid="apply-auto-btn">
                <Wand2 size={13} className="mr-1" /> Reset to auto-detect
              </Button>
            </div>

            {/* Missing-required warning */}
            {missingRequired.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="missing-required-warn">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">
                    {missingRequired.length} required Accounting field(s) unmapped.
                  </div>
                  <div className="mt-0.5">
                    Missing: {missingRequired.map((f) => fields.find((x) => x.key === f)?.label || f).join(', ')}.
                    <br />
                    You can still apply this mapping — affected lines will be flagged for manual review.
                  </div>
                </div>
              </div>
            )}

            {/* Header → field table */}
            <div className="rounded-md border border-slate-200">
              <table className="w-full text-xs" data-testid="mapping-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                    <th className="py-2 px-3 w-1/3">Imported column</th>
                    <th className="py-2 px-3 w-1/3">Accounting field</th>
                    <th className="py-2 px-3">Sample values</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r, i) => (
                    <tr key={r.header || `col-${i}`} className="border-b last:border-0" data-testid={`mapping-row-${i}`}>
                      <td className="py-2 px-3 font-medium text-slate-800">
                        {r.header || <span className="text-slate-400 italic">(blank)</span>}
                      </td>
                      <td className="py-2 px-3">
                        <Select
                          value={r.canonical || IGNORE_KEY}
                          onValueChange={(v) => setMap(r.header, v)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`mapping-select-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={IGNORE_KEY}>— Ignore —</SelectItem>
                            {fields.map((f) => (
                              <SelectItem key={f.key} value={f.key}>
                                {f.label}{f.required ? ' *' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {r.canonical && (
                          <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-700">
                            <CheckCircle2 size={10} /> mapped
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 font-mono text-[11px] text-slate-500">
                        {(() => {
                          const vals = r.sampleValues.filter((v) => v !== null && v !== '').slice(0, 3);
                          if (vals.length === 0) return <span className="italic text-slate-300">—</span>;
                          return vals.map((v, k) => (
                            <div key={k} className="truncate max-w-[220px]">{String(v)}</div>
                          ));
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Save-as-template */}
            <div className="rounded-md border border-slate-200 p-3">
              <Label className="text-xs">Save this column layout as a reusable Template (optional)</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  value={saveTemplateName}
                  onChange={(e) => setSaveTemplateName(e.target.value)}
                  placeholder="e.g. Humana - Statement Layout A"
                  data-testid="save-template-name"
                />
              </div>
              <div className="mt-1 text-[10px] text-slate-500">
                {saveTemplateName.trim()
                  ? <>Saved when you click <b>Apply &amp; Save</b> below. Re-saving the same name updates it. Auto-applied next time you upload this carrier.</>
                  : <>Type a name to remember this layout. It&apos;s auto-suggested next time the same carrier&apos;s statement is uploaded.</>}
              </div>
            </div>
          </div>
        )}

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={applying || loading}
            data-testid="apply-mapping-btn"
          >
            {applying ? (<><Loader2 size={13} className="mr-1 animate-spin" /> Applying…</>) : (<><Save size={13} className="mr-1" /> {saveTemplateName.trim() ? 'Apply & Save Template' : 'Apply Mapping & Import Lines'}</>)}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default MappingWizard;
